import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import type { ApiClient, ArtikelKategorie, Unternehmen } from "@kundeninfo/api-client";
import { Arbeitsdialog, darfAbbrechen, useBeforeUnload, useBestaetigung } from "@kundeninfo/ui";
import { Eingabe, Hinweis, ziehbar } from "@kundeninfo/ui/me-design";
import type { ZiehbarOptionen } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../meHuellen.js";
import { ARTICLE_DIALOG_TEXT, CATEGORY_CARE_TEXT as T } from "./articleDialogTexts.js";
import "./KategorienPflege.css";

/**
 * Die KATEGORIENPFLEGE (Spec Artikelmodul 0.4, „Kategorienpflege (776)“): die Kategorien des Arbeitsunternehmens (777)
 * in der Hülle `Arbeitsdialog panel="artikel-kategorien"`. Je Kategorie eine Zeile mit Ziehgriff (`ziehbar()`), Hoch
 * und Runter für die Tastatur, Name, Zähler „n Artikel“ und Löschen nur bei 0 — eine belegte Kategorie wird umbenannt
 * oder geleert, nie gelöscht (der Server antwortete sonst 409). Unten „Neue Kategorie“.
 *
 * Nichts wird beim Bedienen geschrieben. Gespeichert wird als Ganzes nach EINER Rückfrage, die nennt, was geschieht, und
 * in fester Folge: neue anlegen, Namen ändern, Reihenfolge (`reorderProductCategories` mit der VOLLSTÄNDIGEN Liste des
 * Unternehmens — die zu löschenden stehen dann noch, also hinten), leere löschen. Ein Fehler stoppt die Reihe; der
 * Dialog liest den Stand neu und sagt, woran es lag.
 */
export type KategorienPflegeClient = Pick<ApiClient, "listProductCategories" | "createProductCategory"
  | "renameProductCategory" | "reorderProductCategories" | "deleteProductCategory">;

export type KategorienPflegeProps = {
  client: KategorienPflegeClient;
  /** Das Arbeitsunternehmen — nur seine Kategorien werden gepflegt (777). */
  unternehmen: Unternehmen;
  /** Geschlossen; `geaendert`: Es wurde etwas geschrieben (der Bereich lädt dann neu). */
  onFertig: (geaendert: boolean) => void;
};

/** Eine Zeile: bestehend (`kategorie`) oder neu (`kategorie === null`); `schluessel` bleibt über das Umordnen stabil. */
type Zeile = { schluessel: string; kategorie: ArtikelKategorie | null; name: string };

/** Verschiebt ein Element von `von` nach `nach` — der gemeinsame Weg von Ziehen, Hoch und Runter; die Liste bleibt. */
export function verschiebe<T>(liste: readonly T[], von: number, nach: number): T[] {
  const neu = [...liste];
  const [el] = neu.splice(von, 1);
  if (el !== undefined) neu.splice(nach, 0, el);
  return neu;
}

const alsZeilen = (items: readonly ArtikelKategorie[]): Zeile[] =>
  [...items].sort((a, b) => a.sortIndex - b.sortIndex).map((k) => ({ schluessel: k.id, kategorie: k, name: k.name }));

/** Was beim Speichern geschähe — für die Frage und den Schreibweg. */
function planVon(start: readonly Zeile[], zeilen: readonly Zeile[]) {
  const neu = zeilen.filter((z) => z.kategorie === null);
  const umbenannt = zeilen.filter((z) => z.kategorie !== null && z.name.trim() !== z.kategorie.name);
  const geloescht = start.filter((s) => !zeilen.some((z) => z.schluessel === s.schluessel));
  // Ohne Umordnen stünden die übrigen in ihrer alten Folge und die neuen hinten — dorthin hängt der Server sie an.
  const natuerlich = [...start.filter((s) => !geloescht.includes(s)), ...neu].map((z) => z.schluessel);
  const reihenfolge = zeilen.map((z) => z.schluessel).join("\n") !== natuerlich.join("\n");
  return { neu, umbenannt, geloescht, reihenfolge,
    leer: neu.length === 0 && umbenannt.length === 0 && geloescht.length === 0 && !reihenfolge };
}

const meldungAus = (fehler: unknown): string =>
  (fehler instanceof Error && fehler.message !== "" ? fehler.message : ARTICLE_DIALOG_TEXT.actionFailed);

export function KategorienPflege(p: KategorienPflegeProps): ReactElement {
  const { client, unternehmen } = p;
  const [start, setStart] = useState<Zeile[] | null>(null);
  const [zeilen, setZeilen] = useState<Zeile[]>([]);
  const [fehler, setFehler] = useState<Record<string, string>>({});
  const [meldung, setMeldung] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  /** Wurde schon etwas geschrieben (auch vor einem Fehler)? Dann lädt der Bereich beim Schließen neu. */
  const geschrieben = useRef(false);
  const neuZaehler = useRef(0);
  const { bestaetigen } = useBestaetigung();

  async function lade(): Promise<void> {
    const z = alsZeilen((await client.listProductCategories(unternehmen.id)).items);
    setStart(z); setZeilen(z);
  }
  useEffect(() => {
    lade().catch(() => setMeldung(T.loadError));
    // Einmal je Öffnen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const plan = planVon(start ?? [], zeilen);
  const geaendert = start !== null && !plan.leer;
  useBeforeUnload(geaendert);

  /*
   * Ziehen (776): `ziehbar()` am Behälter der Liste; die App verschiebt selbst im Zustand (`abgelegt` → `verschiebe`),
   * der Helfer setzt nur Griff, Linie und Tastaturweg. Die Optionen lesen den jüngsten Zustand über eine Ref, damit der
   * Effekt nur einmal läuft (Muster me-design `Ziehen.d.ts`).
   */
  const liste = useRef<HTMLOListElement | null>(null);
  const stand = useRef(zeilen);
  stand.current = zeilen;
  const bereit = start !== null;
  useEffect(() => {
    const el = liste.current;
    if (el === null) return undefined;
    const name = (karte: HTMLElement): string => stand.current.find((z) => z.schluessel === karte.dataset.schluessel)?.name
      || T.unnamed;
    const optionen: ZiehbarOptionen = {
      karten: ".kpf__zeile", griff: "[data-griff]", spalten: ".kpf__liste", ordnung: "frei", quittung: false,
      name, beschriftung: (karte) => T.move(name(karte)), schluessel: (karte) => karte.dataset.schluessel ?? "",
      abgelegt: (d) => {
        const vorher = stand.current;
        setZeilen(verschiebe(vorher, d.vonIndex, d.index));
        return () => setZeilen(vorher);
      },
    };
    const zug = ziehbar(el, optionen);
    return () => zug.beenden();
  }, [bereit]);

  const setzeName = (schluessel: string, name: string): void => {
    setZeilen((alt) => alt.map((z) => (z.schluessel === schluessel ? { ...z, name } : z)));
    setFehler((alt) => { const { [schluessel]: _weg, ...rest } = alt; return rest; });
  };
  const neueKategorie = (): void => {
    neuZaehler.current += 1;
    setZeilen((alt) => [...alt, { schluessel: `neu-${neuZaehler.current}`, kategorie: null, name: "" }]);
  };

  async function speichern(): Promise<void> {
    if (laeuft || start === null) return;
    if (plan.leer) { p.onFertig(geschrieben.current); return; }
    const leer = zeilen.filter((z) => z.name.trim() === "");
    if (leer.length > 0) {
      setFehler(Object.fromEntries(leer.map((z) => [z.schluessel, T.nameMissing])));
      return;
    }
    const ja = await bestaetigen({ titel: T.confirm({ order: plan.reihenfolge, renamed: plan.umbenannt.length,
      created: plan.neu.length, deleted: plan.geloescht.length }), text: T.confirmText,
      bestaetigenText: ARTICLE_DIALOG_TEXT.yes, abbrechenText: ARTICLE_DIALOG_TEXT.no });
    if (!ja) return;
    setLaeuft(true); setMeldung(null);
    try {
      const ids = new Map(zeilen.flatMap((z) => (z.kategorie === null ? [] : [[z.schluessel, z.kategorie.id] as const])));
      for (const z of plan.neu) {
        ids.set(z.schluessel, (await client.createProductCategory({ companyId: unternehmen.id, name: z.name.trim() })).id);
        geschrieben.current = true;
      }
      for (const z of plan.umbenannt) {
        await client.renameProductCategory(z.kategorie!.id, z.kategorie!.version, z.name.trim());
        geschrieben.current = true;
      }
      if (plan.reihenfolge) {
        await client.reorderProductCategories(unternehmen.id,
          [...zeilen.map((z) => ids.get(z.schluessel)!), ...plan.geloescht.map((z) => z.kategorie!.id)]);
        geschrieben.current = true;
      }
      for (const z of plan.geloescht) {
        await client.deleteProductCategory(z.kategorie!.id);
        geschrieben.current = true;
      }
      p.onFertig(true);
    } catch (f) {
      // Die Reihe steht; der Dialog zeigt den Stand des Servers und den Grund.
      setMeldung(meldungAus(f));
      await lade().catch(() => undefined);
    } finally {
      setLaeuft(false);
    }
  }

  async function abbrechen(): Promise<void> {
    if (laeuft) return;
    if (await darfAbbrechen(geaendert)) p.onFertig(geschrieben.current);
  }

  return (
    <Arbeitsdialog titel={T.title(unternehmen.shortCode)} panel="artikel-kategorien" onAbbrechen={abbrechen}
      abbrechenGesperrt={laeuft}
      {...(meldung === null ? {} : { meldungen: <Hinweis art="fehler">{meldung}</Hinweis> })}
      fuss={(
        <>
          <KiKnopf deaktiviert={laeuft} onClick={() => { void abbrechen(); }}>{ARTICLE_DIALOG_TEXT.cancel}</KiKnopf>
          <KiKnopf art="primaer" deaktiviert={laeuft || start === null} onClick={() => { void speichern(); }}>
            {ARTICLE_DIALOG_TEXT.save}
          </KiKnopf>
        </>
      )}>
      {start === null ? (meldung === null && <p role="status">{ARTICLE_DIALOG_TEXT.loading}</p>) : (
        <>
          {zeilen.length === 0 && <p className="kpf__leer">{T.empty}</p>}
          <ol ref={liste} className="kpf__liste" aria-label={T.listLabel} data-name={T.listLabel}>
            {zeilen.map((z, i) => {
              const name = z.name.trim() === "" ? T.unnamed : z.name;
              const fehlerText = fehler[z.schluessel];
              return (
                <li key={z.schluessel} className="kpf__zeile" data-schluessel={z.schluessel}>
                  <button type="button" className="kpf__griff" data-griff="">{T.grip}</button>
                  <KiKnopf art="leise" kompakt className="kpf__pfeil" name={T.up(name)} deaktiviert={laeuft || i === 0}
                    onClick={() => setZeilen((alt) => verschiebe(alt, i, i - 1))}>{T.upSymbol}</KiKnopf>
                  <KiKnopf art="leise" kompakt className="kpf__pfeil" name={T.down(name)}
                    deaktiviert={laeuft || i === zeilen.length - 1}
                    onClick={() => setZeilen((alt) => verschiebe(alt, i, i + 1))}>{T.downSymbol}</KiKnopf>
                  <Eingabe className="kpf__name" aria-label={T.name(i + 1)} wert={z.name} gesperrt={laeuft}
                    {...(fehlerText === undefined ? {} : { fehler: fehlerText })}
                    onGeaendert={(d) => setzeName(z.schluessel, d.wert)} />
                  <span className="kpf__zaehler">{T.usage(z.kategorie?.usage ?? 0)}</span>
                  {(z.kategorie === null || z.kategorie.usage === 0) ? (
                    <KiKnopf art="leise" kompakt name={T.remove(name)} deaktiviert={laeuft}
                      onClick={() => setZeilen((alt) => alt.filter((x) => x.schluessel !== z.schluessel))}>
                      {T.removeText}
                    </KiKnopf>
                  ) : <span className="kpf__platz" aria-hidden="true" />}
                </li>
              );
            })}
          </ol>
          <KiKnopf deaktiviert={laeuft} onClick={neueKategorie}>{T.newCategory}</KiKnopf>
        </>
      )}
    </Arbeitsdialog>
  );
}
